import { APIClient, ApiQueryParams, DefaultQueryParams } from '@frontend/api-utils';
import {
    CreateEquipment,
    EquipmentListResponse,
    EquipmentQueryParams,
    EquipmentWithProductListResponse,
    IEquipment,
    UpdateEquipment
} from '@frontend/equipment/types';

export class EquipmentClient extends APIClient {
    public static readonly endpoint = '/equipment-api/v1';

    public static async fetchEquipments(queryParams?: ApiQueryParams<EquipmentQueryParams>): Promise<EquipmentListResponse> {
        return await this.apiPaginated<EquipmentListResponse, EquipmentQueryParams>(`${this.endpoint}/equipments`, queryParams);
    }
    public static async resolveEquipments(ids: string[]): Promise<EquipmentListResponse> {
        const response = await this.post(`${this.endpoint}/equipments-resolve`, { ids: ids });
        return await this.handleResponse<EquipmentListResponse>(response);
    }

    public static async resolveEquipmentsWithProduct(ids: string[], field: string = 'id'): Promise<EquipmentWithProductListResponse> {
        const response = await this.post(`${this.endpoint}/equipments-resolve-with-product`, { ids: ids, field });
        return await this.handleResponse<EquipmentWithProductListResponse>(response);
    }

    public static async updateEquipments(ids: string[], update: UpdateEquipment): Promise<EquipmentListResponse> {
        const response = await this.post(`${this.endpoint}/equipments-update`, { ...update, ids: ids });
        return await this.handleResponse<EquipmentListResponse>(response);
    }

    public static async deleteEquipments(ids: string[]): Promise<void> {
        const response = await this.post(`${this.endpoint}/equipments-delete`, { ids: ids });
        await this.handleVoidResponse(response);
    }

    public static async postEquipment(accountId: string, equipment: CreateEquipment): Promise<IEquipment> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/equipments`, equipment);
        return await this.handleResponse<IEquipment>(response);
    }

    public static async fetchEquipmentsNotInGroup(
        accountId: string,
        groupId: string,
        queryParams?: ApiQueryParams<DefaultQueryParams>
    ): Promise<EquipmentListResponse> {
        return await this.apiPaginated<EquipmentListResponse, DefaultQueryParams>(
            `${this.endpoint}/accounts/${accountId}/groups/${groupId}/equipments`,
            queryParams
        );
    }

    public static async fetchEquipment(accountId: string, equipmentId: string): Promise<IEquipment> {
        const response = await this.fetch(`${this.endpoint}/accounts/${accountId}/equipments/${equipmentId}`);
        return await this.handleResponse<IEquipment>(response);
    }

    public static async patchEquipment(accountId: string, equipmentId: string, equipment: UpdateEquipment): Promise<IEquipment> {
        const response = await this.patch(`${this.endpoint}/accounts/${accountId}/equipments/${equipmentId}`, equipment);
        return await this.handleResponse<IEquipment>(response);
    }

    public static async deleteEquipment(accountId: string, equipmentId: string): Promise<void> {
        const response = await this.delete(`${this.endpoint}/accounts/${accountId}/equipments/${equipmentId}`);
        await this.handleVoidResponse(response);
    }

    public static async postIoTSeed(accountId: string, iotId: string, body: { account_id: string }): Promise<void> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/iots/${iotId}/seed`, body);
        return this.handleResponse(response);
    }
}
